import { MatchState, Player, Inning, Delivery } from "../matchTypes";

export interface LiveBatter {
  id: string;
  name: string;
  runs: number;
  balls: number;
  fours: number;
  sixes: number;
  strikeRate: string;
  isStriker: boolean;
}

export interface LiveBowler {
  id: string;
  name: string;
  overs: string;
  maidens: number;
  runs: number;
  wickets: number;
  economy: string;
}

export interface OverBall {
  display: string;
  runs: number;
  isWicket: boolean;
  isWide: boolean;
  isNoBall: boolean;
  isLegal: boolean;
  extraRuns: number;
  batRuns: number;
  wallBonus: boolean;
}

export interface LastWicket {
  name: string;
  runs: number;
  balls: number;
  dismissal: string;
}

export interface PublicLiveState {
  batters: LiveBatter[];
  bowler: LiveBowler | null;
  overBalls: OverBall[];
  score: string;
  overs: string;
  wickets: number;
  totalRuns: number;
  totalBalls: number;
  crr: number;
  target: number | null;
  runsNeeded: number | null;
  ballsRemaining: number | null;
  rrr: number | null;
  partnership: { runs: number; balls: number };
  lastWicket: LastWicket | null;
  isIndoor: boolean;
  battingTeamName: string;
}

function formatOvers(totalBalls: number, ballsPerOver: number): string {
  const overs = Math.floor(totalBalls / ballsPerOver);
  const balls = totalBalls % ballsPerOver;
  return balls > 0 ? `${overs}.${balls}` : `${overs}`;
}

function strikeRate(runs: number, balls: number): string {
  if (balls === 0) return "—";
  return ((runs / balls) * 100).toFixed(1);
}

function computeMaidens(deliveries: Delivery[], bowlerId: string, ballsPerOver: number): number {
  let maidens = 0;
  let ballsInOver = 0;
  let runsInOver = 0;

  for (const d of deliveries) {
    if (d.bowlerId !== bowlerId) continue;
    runsInOver += d.runs;
    if (d.isLegal) {
      ballsInOver++;
    }
    if (ballsInOver === ballsPerOver) {
      if (runsInOver === 0) maidens++;
      ballsInOver = 0;
      runsInOver = 0;
    }
  }
  return maidens;
}

function getLastWicket(inn: Inning, battingTeamPlayers: Player[]): LastWicket | null {
  for (let i = inn.deliveries.length - 1; i >= 0; i--) {
    const d = inn.deliveries[i];
    if (d.isWicket && d.batterId) {
      const player = battingTeamPlayers.find(p => p.id === d.batterId);
      if (player) {
        return {
          name: player.name,
          runs: player.runs,
          balls: player.balls,
          dismissal: d.dismissal || "out",
        };
      }
    }
  }
  return null;
}

function getPartnershipFromInning(
  inn: Inning,
  strikerId: string | null,
  nonStrikerId: string | null
): { runs: number; balls: number } {
  if (!strikerId || !nonStrikerId) return { runs: 0, balls: 0 };

  let runs = 0;
  let balls = 0;
  let curStriker = strikerId;
  let curNonStriker = nonStrikerId;

  for (let i = inn.deliveries.length - 1; i >= 0; i--) {
    const d = inn.deliveries[i];
    if (d.isWicket) break;
    if (!d.isLegal) continue;

    runs += d.runs;
    balls += 1;

    let rotation = d.runsForRotation ?? d.runs;
    if (d.wallBonus) rotation = 1;
    else if (d.extraType === "noball" || d.extraType === "wide") rotation = Math.max(0, d.runs - 1);

    if (rotation === 1 || rotation === 3 || rotation === 5) {
      const tmp = curStriker;
      curStriker = curNonStriker;
      curNonStriker = tmp;
    }
    if (balls % 6 === 0) {
      const tmp = curStriker;
      curStriker = curNonStriker;
      curNonStriker = tmp;
    }
    if (curStriker !== strikerId && curStriker !== nonStrikerId) break;
  }

  return { runs, balls };
}

function getCurrentOverBalls(inn: Inning, ballsPerOver: number): OverBall[] {
  const deliveries = inn.deliveries;
  if (deliveries.length === 0) return [];

  const totalLegalBalls = deliveries.filter(d => d.isLegal).length;
  if (totalLegalBalls === 0) {
    return deliveries.map(d => ({
      display: d.display, runs: d.runs, isWicket: d.isWicket,
      isWide: d.extraType === "wide", isNoBall: d.extraType === "noball",
      isLegal: d.isLegal,
      extraRuns: d.extraType === "wide" || d.extraType === "noball" ? 1 : 0,
      batRuns: d.runs - (d.extraType === "wide" || d.extraType === "noball" ? 1 : 0),
      wallBonus: !!d.wallBonus,
    }));
  }

  const legalBallsInCurrentOver = totalLegalBalls % ballsPerOver;
  const legalBallsInPrevOver = totalLegalBalls - (legalBallsInCurrentOver === 0 ? ballsPerOver : legalBallsInCurrentOver);

  let legalBallsSeen = 0;
  let firstLegalIdx = -1;

  for (let i = 0; i < deliveries.length; i++) {
    if (deliveries[i].isLegal) {
      legalBallsSeen++;
      if (legalBallsSeen === legalBallsInPrevOver + 1) {
        firstLegalIdx = i;
        break;
      }
    }
  }

  if (firstLegalIdx === -1) return [];

  let startIdx = firstLegalIdx;
  while (startIdx > 0 && !deliveries[startIdx - 1].isLegal) {
    startIdx--;
  }

  const overDeliveries = deliveries.slice(startIdx);
  return overDeliveries.map(d => ({
    display: d.display,
    runs: d.runs,
    isWicket: d.isWicket,
    isWide: d.extraType === "wide",
    isNoBall: d.extraType === "noball",
    isLegal: d.isLegal,
    extraRuns: d.extraType === "wide" || d.extraType === "noball" ? 1 : 0,
    batRuns: d.runs - (d.extraType === "wide" || d.extraType === "noball" ? 1 : 0),
    wallBonus: !!d.wallBonus,
  }));
}

export function getPublicLiveState(
  matchData: MatchState,
  teamAName: string,
  teamBName: string
): PublicLiveState | null {
  const currentInningsIdx = matchData.currentInnings ?? 0;
  const inn = matchData.innings?.[currentInningsIdx];
  if (!inn) return null;

  const bpo = (matchData as any).ballsPerOver || 6;
  const isIndoor = matchData.format === "INDOOR";
  const battingTeamPlayers = inn.battingTeam === "A" ? matchData.teamAPlayers : matchData.teamBPlayers;
  const bowlingTeamPlayers = inn.battingTeam === "A" ? matchData.teamBPlayers : matchData.teamAPlayers;
  const battingTeamName = inn.battingTeam === "A" ? teamAName : teamBName;

  const batters: LiveBatter[] = [];
  if (matchData.strikerId) {
    const p = battingTeamPlayers.find(pl => pl.id === matchData.strikerId);
    if (p) {
      batters.push({
        id: p.id, name: p.name, runs: p.runs, balls: p.balls,
        fours: p.fours, sixes: p.sixes,
        strikeRate: strikeRate(p.runs, p.balls), isStriker: true,
      });
    }
  }
  if (matchData.nonStrikerId) {
    const p = battingTeamPlayers.find(pl => pl.id === matchData.nonStrikerId);
    if (p) {
      batters.push({
        id: p.id, name: p.name, runs: p.runs, balls: p.balls,
        fours: p.fours, sixes: p.sixes,
        strikeRate: strikeRate(p.runs, p.balls), isStriker: false,
      });
    }
  }

  let bowler: LiveBowler | null = null;
  if (matchData.bowlerId) {
    const bp = bowlingTeamPlayers.find(pl => pl.id === matchData.bowlerId);
    if (bp) {
      const bowlerDeliveries = inn.deliveries.filter(d => d.bowlerId === matchData.bowlerId);
      const legalBalls = bowlerDeliveries.filter(d => d.isLegal).length;
      const runsConceded = bowlerDeliveries.reduce((sum, d) => sum + d.runs, 0);
      const wickets = bowlerDeliveries.filter(d => d.isWicket && d.isLegal && d.dismissalType !== "Run Out").length;
      const maidens = computeMaidens(inn.deliveries, matchData.bowlerId, bpo);
      bowler = {
        id: bp.id, name: bp.name,
        overs: formatOvers(legalBalls, bpo),
        maidens,
        runs: runsConceded,
        wickets,
        economy: legalBalls > 0 ? (runsConceded / (legalBalls / bpo)).toFixed(2) : "—",
      };
    }
  }

  const overBalls = getCurrentOverBalls(inn, bpo);

  const totalRuns = inn.totalRuns || 0;
  const totalBalls = inn.totalBalls || 0;
  const wickets = inn.wickets || 0;
  const crr = totalBalls > 0 ? totalRuns / (totalBalls / bpo) : 0;

  let target: number | null = null;
  let runsNeeded: number | null = null;
  let ballsRemaining: number | null = null;
  let rrr: number | null = null;

  if (currentInningsIdx > 0) {
    const firstInn = matchData.innings[0];
    if (firstInn) {
      target = (firstInn.totalRuns || 0) + 1;
      runsNeeded = target - totalRuns;
      const oversLimit = inn.oversLimit ?? matchData.oversLimit ?? 20;
      ballsRemaining = oversLimit * bpo - totalBalls;
      if (runsNeeded > 0 && ballsRemaining > 0) {
        rrr = (runsNeeded * bpo) / ballsRemaining;
      } else if (runsNeeded <= 0) {
        rrr = 0;
      }
    }
  }

  const partnership = getPartnershipFromInning(inn, matchData.strikerId, matchData.nonStrikerId);
  const lastWicket = getLastWicket(inn, battingTeamPlayers);

  return {
    batters,
    bowler,
    overBalls,
    score: `${totalRuns}/${wickets}`,
    overs: formatOvers(totalBalls, bpo),
    wickets,
    totalRuns,
    totalBalls,
    crr,
    target,
    runsNeeded,
    ballsRemaining,
    rrr,
    partnership,
    lastWicket,
    isIndoor,
    battingTeamName,
  };
}
